import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Building2, MessageSquare, RefreshCw, Users, History, BarChart3 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CompaniesPanel } from "@/components/owner/CompaniesPanel";
import { RequestsPanel } from "@/components/owner/RequestsPanel";
import { PeoplePanel } from "@/components/owner/PeoplePanel";
import { ActivityPanel } from "@/components/owner/ActivityPanel";
import { VisitsPanel } from "@/components/owner/VisitsPanel";

const HEAD = {
  companies: ["Every company on Cross Jobs", "Only you can see this page. Open a company to work inside its dashboard; custom features apply to that company only."],
  visits: ["Website visits", "Private counter for your public pages. Only you can see it."],
  requests: ["Change requests", "Requests companies send from Settings. Tick one off when it's done."],
  people: ["People", "Find any supervisor or worker and set them a new password."],
  activity: ["Activity log", "Everything you've changed inside companies and on this page."],
};

const tabCls = "shrink-0 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none data-[state=active]:bg-transparent px-2 sm:px-3 py-3 text-sm font-medium text-muted-foreground";

export default function OwnerDashboard() {
  const { user, logout } = useAuth();
  const [companies, setCompanies] = useState(null);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState("companies");

  const load = useCallback(async () => {
    try {
      const [cs, rs] = await Promise.all([api.ownerCompanies(), api.ownerRequests()]);
      setCompanies(cs); setRequests(rs);
    } catch { toast.error("Could not load the admin page"); setCompanies((c) => c || []); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const open = requests.filter((r) => r.status === "open").length;

  return (
    <Tabs value={tab} onValueChange={setTab} className="min-h-screen bg-background" data-testid="owner-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/85 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <BrandMark />
          <span className="hidden sm:inline rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-semibold">Owner admin</span>
          <span className="ml-auto hidden md:inline text-sm text-muted-foreground">{user?.email}</span>
          <Button variant="ghost" size="icon" className="rounded-full" onClick={load} aria-label="Refresh" data-testid="owner-refresh-btn"><RefreshCw className="w-5 h-5" /></Button>
          <Button variant="ghost" size="icon" className="rounded-full" onClick={logout} aria-label="Sign out" data-testid="logout-btn"><LogOut className="w-5 h-5" /></Button>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <TabsList className="h-auto bg-transparent p-0 gap-1 flex justify-start rounded-none overflow-x-auto max-w-full [scrollbar-width:none]">
            <TabsTrigger value="companies" className={tabCls} data-testid="owner-tab-companies"><Building2 className="w-4 h-4 mr-1.5" /> Companies {companies && <span className="ml-1.5 font-mono text-xs">{companies.length}</span>}</TabsTrigger>
            <TabsTrigger value="people" className={tabCls} data-testid="owner-tab-people"><Users className="w-4 h-4 mr-1.5" /> People</TabsTrigger>
            <TabsTrigger value="activity" className={tabCls} data-testid="owner-tab-activity"><History className="w-4 h-4 mr-1.5" /> Activity</TabsTrigger>
            <TabsTrigger value="visits" className={tabCls} data-testid="owner-tab-visits"><BarChart3 className="w-4 h-4 mr-1.5" /> Visits</TabsTrigger>
            <TabsTrigger value="requests" className={tabCls} data-testid="owner-tab-requests">
              <MessageSquare className="w-4 h-4 mr-1.5" /> <span className="sm:hidden">Requests</span><span className="hidden sm:inline">Change requests</span>
              {open > 0 && <span className="ml-1.5 rounded-full bg-amber-500 text-white px-1.5 text-[11px] font-bold" data-testid="owner-open-count">{open}</span>}
            </TabsTrigger>
          </TabsList>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{HEAD[tab][0]}</h1>
          <p className="text-sm text-muted-foreground mt-1">{HEAD[tab][1]}</p>
        </div>
        {companies === null ? <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div> : (
          <>
            <TabsContent value="companies" className="mt-0"><CompaniesPanel companies={companies} setCompanies={setCompanies} /></TabsContent>
            <TabsContent value="requests" className="mt-0"><RequestsPanel requests={requests} setRequests={setRequests} /></TabsContent>
            <TabsContent value="people" className="mt-0"><PeoplePanel /></TabsContent>
            <TabsContent value="activity" className="mt-0">{tab === "activity" && <ActivityPanel />}</TabsContent>
            <TabsContent value="visits" className="mt-0">{tab === "visits" && <VisitsPanel />}</TabsContent>
          </>
        )}
      </main>
    </Tabs>
  );
}
